import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash, X, Upload, Video, GripVertical, Save, Edit, Eye, ChevronDown, ChevronRight, Share2 } from 'lucide-react';
import './ReelVideos.css';
import { reelAPI } from '../../../config/reel-video-api';
import { productsAPI } from '../../../config/product-api';
import { toast } from 'react-toastify';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import { getAdminRegion } from '../../../constants/stores';

const mb = (bytes) => (bytes / 1024 / 1024).toFixed(2);
const formatCompressionSize = (bytes) => bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    : `${(bytes / 1024).toFixed(1)} KB`;
const formatDuration = (seconds) => `${Number(seconds || 0).toFixed(1)}s`;
const logCompressionResult = (scope, file, finalFile, status, reason = '', durationSec = null) => {
    const savedPct = file.size > 0 ? ((1 - (finalFile.size / file.size)) * 100).toFixed(1) : '0.0';
    const reasonPart = reason ? ` | reason: ${reason}` : '';
    console.log(`[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(finalFile.size)} | saved: ${savedPct}% | duration: ${durationSec !== null ? formatDuration(durationSec) : 'n/a'} | status: ${status}${reasonPart} | file: ${file.name}`);
};
const logCompressionSkip = (scope, file, reason) => {
    console.log(`[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(file.size)} | saved: 0.0% | duration: n/a | status: original kept | reason: ${reason} | file: ${file.name}`);
};

const ReelVideos = ({ adminData }) => {
    const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_REEL_VIDEOS);
    const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_REEL_VIDEOS);
    const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_REEL_VIDEOS);
    const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_REEL_VIDEOS);
    const storeRegion = getAdminRegion(adminData);

    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);

    const [reelToDelete, setReelToDelete] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editingReel, setEditingReel] = useState(null);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [draggedReel, setDraggedReel] = useState(null);
    const [dragOverReel, setDragOverReel] = useState(null);

    const [activeReels, setActiveReels] = useState([]);
    const [inactiveReels, setInactiveReels] = useState([]);
    const [isInactiveExpanded, setIsInactiveExpanded] = useState(false);

    const [isInactiveLoading, setIsInactiveLoading] = useState(false);
    const [loadingReels, setLoadingReels] = useState(false);
    const [savingReel, setSavingReel] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [activeCount, setActiveCount] = useState(0);
    const [inactiveCount, setInactiveCount] = useState(0);

    const ffmpegRef = useRef(null);
    const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
    const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
    const [compressingVideo, setCompressingVideo] = useState(false);
    const isCancelledRef = useRef(false);

    const isProcessing = compressingVideo || generatingThumbnail;

    const [formData, setFormData] = useState({
        video: null,
        videoPreview: null,
        thumbnail: null,
        thumbnailPreview: null,
        title: "",
        isActive: true,
        productId: "",
        region: "qatar",
    });

    const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
    const [isProductViewDialogOpen, setIsProductViewDialogOpen] = useState(false);
    const [selectedProductInfo, setSelectedProductInfo] = useState(null);
    const [productSearch, setProductSearch] = useState('');
    const [productResults, setProductResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);

    const [activeCurrentPage, setActiveCurrentPage] = useState(1);
    const [activePagination, setActivePagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNext: false,
        hasPrev: false
    });
    const [inactiveCurrentPage, setInactiveCurrentPage] = useState(1);
    const [inactivePagination, setInactivePagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNext: false,
        hasPrev: false
    });

    const fetchAllActiveReels = async (page = 1) => {
        try {
            setLoadingReels(true);
            const response = await reelAPI.getActiveReels(page, 20, storeRegion);
            if (response.success) {
                const activeReelVideos = (response.data || []).map(reel => ({
                    ...reel,
                    originalOrder: reel.order
                }));
                setActiveReels(activeReelVideos);
                setActivePagination(response.pagination || {
                    currentPage: 1,
                    totalPages: 1,
                    totalCount: 0,
                    hasNext: false,
                    hasPrev: false
                });
                setActiveCount(response.activeCount || response.pagination?.totalCount || activeReelVideos.length || 0);
                setInactiveCount(response.inactiveCount || 0);
            }
        } catch (error) {
            console.error('Error fetching reels:', error);
        } finally {
            setLoadingReels(false);
        }
    };

    const fetchInactiveReels = async (page = 1) => {
        setIsInactiveLoading(true);
        try {
            const response = await reelAPI.getInactiveReels(page, 20, storeRegion);
            if (response.success) {
                setInactiveReels(response.data || []);
                setInactivePagination(response.pagination || {
                    currentPage: 1,
                    totalPages: 1,
                    totalCount: 0,
                    hasNext: false,
                    hasPrev: false
                });
            }
        } catch (error) {
            console.error('Error fetching inactive reels:', error);
        } finally {
            setIsInactiveLoading(false);
        }
    };

    useEffect(() => {
        if (!canView || !isInactiveExpanded) return;
        fetchInactiveReels(inactiveCurrentPage);
    }, [canView, isInactiveExpanded, inactiveCurrentPage, storeRegion]);

    useEffect(() => {
        if (!canView) return;
        fetchAllActiveReels(activeCurrentPage);
    }, [canView, activeCurrentPage, storeRegion]);

    useEffect(() => {
        return () => {
            try {
                ffmpegRef.current?.terminate?.();
            } catch { }
        };
    }, []);

    const handleActiveNextPage = () => {
        if (activePagination.hasNext) {
            setActiveCurrentPage(prev => prev + 1);
        }
    };

    const handleActivePrevPage = () => {
        if (activePagination.hasPrev) {
            setActiveCurrentPage(prev => prev - 1);
        }
    };

    const handleToggleInactive = () => {
        setIsInactiveExpanded(!isInactiveExpanded);
        if (!isInactiveExpanded) {
            setInactiveCurrentPage(1);
            fetchInactiveReels(1);
        } else {
            setInactiveReels([]);
        }
    };

    const handleInactiveNextPage = () => {
        if (inactivePagination.hasNext) {
            setInactiveCurrentPage(prev => prev + 1);
        }
    };

    const handleInactivePrevPage = () => {
        if (inactivePagination.hasPrev) {
            setInactiveCurrentPage(prev => prev - 1);
        }
    };

    const loadFFmpegIfNeeded = async () => {
        if (ffmpegRef.current?.loaded) return;

        const { FFmpeg } = await import('@ffmpeg/ffmpeg');
        const { toBlobURL } = await import('@ffmpeg/util');

        ffmpegRef.current = new FFmpeg();
        const ffmpeg = ffmpegRef.current;

        let lastMilestone = 0;
        ffmpeg.on('progress', ({ progress }) => {
            const pct = Math.floor(progress * 100);
            if (pct >= lastMilestone + 25) {
                lastMilestone = Math.floor(pct / 25) * 25;
                console.log(`[Reel Video] Compressing... ${lastMilestone}%`);
            }
        });

        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
        await ffmpeg.load({
            coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
            wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });

        setFfmpegLoaded(true);
    };

    const handleVideoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('video/')) {
            toast.error('Please select a valid video file');
            return;
        }

        if (file.size > 6 * 1024 * 1024) {
            toast.error('Video exceeds the 6MB limit');
            return;
        }

        setFormData(prev => ({
            ...prev,
            video: null,
            videoPreview: null,
            thumbnail: null,
            thumbnailPreview: null
        }));

        isCancelledRef.current = false;
        setCompressingVideo(true);

        try {
            await loadFFmpegIfNeeded();

            let finalVideo = file;
            if (file.size > 1 * 1024 * 1024) {
                const compressionStartedAt = Date.now();
                console.log(`[Reel Video] Compressing: ${file.name} (${mb(file.size)} MB)`);
                const compressedBlob = await compressVideo(file);
                if (isCancelledRef.current) return;

                const compressedFile = new File([compressedBlob], file.name, { type: 'video/mp4' });
                const usedCompressedVideo = compressedFile.size < file.size;

                if (usedCompressedVideo) {
                    finalVideo = compressedFile;
                }
                const durationSec = (Date.now() - compressionStartedAt) / 1000;
                logCompressionResult(
                    'Reel Video',
                    file,
                    finalVideo,
                    usedCompressedVideo ? 'compressed' : 'original kept',
                    usedCompressedVideo ? '' : 'already optimized',
                    durationSec
                );
            } else {
                logCompressionSkip('Reel Video', file, 'limit guard: below 1 MB threshold');
            }

            if (isCancelledRef.current) return;

            const videoURL = URL.createObjectURL(finalVideo);
            setFormData(prev => ({ ...prev, video: finalVideo, videoPreview: videoURL }));
            setCompressingVideo(false);

            setGeneratingThumbnail(true);
            const thumbnailBlob = await generateThumbnail(finalVideo);
            if (isCancelledRef.current) return;

            const thumbnailFile = new File([thumbnailBlob], `thumb_${file.name}`, { type: 'video/mp4' });
            const thumbURL = URL.createObjectURL(thumbnailFile);
            setFormData(prev => ({ ...prev, thumbnail: thumbnailFile, thumbnailPreview: thumbURL }));

            console.log(`[Reel Thumbnail] Generated | size: ${formatCompressionSize(thumbnailFile.size)}`);
        } catch (error) {
            if (!isCancelledRef.current) {
                console.error('Video processing error:', error);
                toast.error('Video processing failed');
            }
        } finally {
            if (!isCancelledRef.current) {
                setCompressingVideo(false);
                setGeneratingThumbnail(false);
            }
        }
    };


    const getVideoDuration = (file) => {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => { URL.revokeObjectURL(video.src); resolve(video.duration || 10); };
            video.onerror = () => { URL.revokeObjectURL(video.src); resolve(10); };
            video.src = URL.createObjectURL(file);
        });
    };

    const compressVideo = async (videoFile) => {
        const startedAt = Date.now();
        const { fetchFile } = await import('@ffmpeg/util');
        const ffmpeg = ffmpegRef.current;

        const input = `in_${Date.now()}.mp4`;
        const output = `out_${Date.now()}.mp4`;

        const duration = await getVideoDuration(videoFile);

        await ffmpeg.writeFile(input, await fetchFile(videoFile));

        const audioBitrateKbps = 96;

        // Target 80% of original, clamped between 1.2MB and 1.8MB
        const targetBytes = Math.min(
            Math.max(videoFile.size * 0.8, 1.2 * 1024 * 1024),
            1.8 * 1024 * 1024
        );
        const totalBitrateKbps = Math.floor((targetBytes * 8) / duration / 1000);
        const videoBitrateKbps = Math.max(300, totalBitrateKbps - audioBitrateKbps);

        console.log(`▶ ${mb(videoFile.size)} MB | ${duration.toFixed(1)}s | target ${(targetBytes / 1024 / 1024).toFixed(2)} MB | ${videoBitrateKbps}k video`);

        const ffmpegArgs = [
            '-i', input,
            '-c:v', 'libx264',
            '-preset', 'superfast',
            '-b:v', `${videoBitrateKbps}k`,
            '-maxrate', `${Math.floor(videoBitrateKbps * 1.3)}k`,
            '-bufsize', `${videoBitrateKbps * 2}k`,
            '-vf', 'scale=trunc(iw/2)*2:trunc(ih/2)*2',
            '-pix_fmt', 'yuv420p',
            '-movflags', 'faststart',
            '-c:a', 'aac', '-b:a', `${audioBitrateKbps}k`,
            output
        ];

        await ffmpeg.exec(ffmpegArgs);

        const data = await ffmpeg.readFile(output);
        await ffmpeg.deleteFile(input);
        await ffmpeg.deleteFile(output);

        const blob = new Blob([data.buffer], { type: 'video/mp4' });
        const outputMB = blob.size / (1024 * 1024);
        const rangeStatus = outputMB <= 1 ? 'under 1MB' : outputMB <= 2 ? 'in range (1-2MB)' : 'over 2MB';
        const elapsedSec = ((Date.now() - startedAt) / 1000).toFixed(1);
        console.log(`[Reel Video] | original: ${formatCompressionSize(videoFile.size)} | final: ${formatCompressionSize(blob.size)} | saved: ${((1 - blob.size / videoFile.size) * 100).toFixed(1)}% | duration: ${elapsedSec}s | status: processed | reason: ${rangeStatus} | file: ${videoFile.name}`);

        return blob;
    };

    const generateThumbnail = async (videoFile) => {
        try {
            const { fetchFile } = await import('@ffmpeg/util');
            const ffmpeg = ffmpegRef.current;

            const input = `thumb_in_${Date.now()}.mp4`;
            const output = `thumb_out_${Date.now()}.mp4`;

            await ffmpeg.writeFile(input, await fetchFile(videoFile));

            await ffmpeg.exec([
                '-i', input,
                '-t', '5',
                '-vf', 'scale=200:-2,fps=15',
                '-c:v', 'libx264',
                '-preset', 'fast',
                '-profile:v', 'baseline',
                '-level', '3.0',
                '-pix_fmt', 'yuv420p',
                '-movflags', 'faststart',
                '-an',
                output
            ]);

            const data = await ffmpeg.readFile(output);
            await ffmpeg.deleteFile(input);
            await ffmpeg.deleteFile(output);

            return new Blob([data.buffer], { type: 'video/mp4' });
        } catch (error) {
            console.error('Thumbnail generation error:', error);
            throw error;
        }
    };

    const handleSaveReel = async () => {
        try {
            if (!formData.title || formData.title.trim().length === 0) {
                toast.error("Please enter a title");
                return;
            }

            if (!editMode) {
                if (!formData.video && !formData.videoPreview) {
                    toast.error('Please select a video');
                    return;
                }

                if (!formData.thumbnail && !formData.thumbnailPreview) {
                    toast.error('Please select a thumbnail');
                    return;
                }
            }

            setSavingReel(true);

            let response;

            if (editMode) {
                response = await reelAPI.updateReel(editingReel._id, {
                    title: formData.title,
                    isActive: formData.isActive,
                    productId: formData.productId,
                    region: storeRegion,
                });
            } else {
                const payload = {
                    ...formData,
                    title: formData.title,
                    productId: formData.productId,
                    region: storeRegion,
                };

                response = await reelAPI.createReel(payload);
            }

            if (response.success) {
                toast.success(editMode ? 'Reel updated successfully!' : 'Reel created successfully!');
                handleCloseDialog();
                fetchAllActiveReels(activeCurrentPage);
                if (isInactiveExpanded) fetchInactiveReels(inactiveCurrentPage);
            } else {
                toast.error(response.msg || 'Action failed');
            }

        } catch (error) {
            console.error('Error saving reel:', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setSavingReel(false);
        }
    };

    const handleShareReel = (reel) => {
        const url = `${window.location.origin}/reel/${reel._id}`;
        navigator.clipboard.writeText(url)
            .then(() => {
                toast.success("Reel video link copied!");
            })
            .catch((err) => {
                console.error("Failed to copy link:", err);
                toast.error("Failed to copy link");
            });
    };

    const fetchProductDetails = async (productId) => {
        if (!productId) {
            setSelectedProductInfo(null);
            return;
        }
        try {
            const response = await productsAPI.getProductById(productId);
            if (response.success && response.data) {
                setSelectedProductInfo(response.data);
            } else {
                const list = await productsAPI.getProductsList({ page: 1, limit: 5, search: String(productId), region: storeRegion });
                if (list.success && list.data?.length) {
                    setSelectedProductInfo(list.data[0]);
                } else {
                    setSelectedProductInfo(null);
                }
            }
        } catch (err) {
            console.error('Error fetching product details:', err);
            setSelectedProductInfo(null);
        }
    };

    const searchProducts = async (query) => {
        setProductSearch(query);
        if (!query || query.trim().length < 2) {
            setProductResults([]);
            return;
        }
        try {
            setSearchingProducts(true);
            const response = await productsAPI.getProductsList({ page: 1, limit: 15, search: query.trim(), region: storeRegion });
            if (response.success) {
                setProductResults(response.data || []);
            } else {
                setProductResults([]);
            }
        } catch (err) {
            setProductResults([]);
        } finally {
            setSearchingProducts(false);
        }
    };

    const handleProductSave = (product) => {
        if (!product) {
            setFormData((prev) => ({ ...prev, productId: '' }));
            setSelectedProductInfo(null);
            setIsProductDialogOpen(false);
            return;
        }
        const productId = String(product.productId || product._id);
        setFormData((prev) => ({ ...prev, productId }));
        setSelectedProductInfo(product);
        setIsProductDialogOpen(false);
        setProductSearch('');
        setProductResults([]);
    };

    const removeSelectedProduct = () => {
        setFormData((prev) => ({ ...prev, productId: '' }));
        setSelectedProductInfo(null);
        setIsProductViewDialogOpen(false);
    };

    const handleCreate = () => {
        resetReelForm();
        setShowDialog(true);
    };

    const handleEdit = (reel) => {
        setEditMode(true);
        setEditingReel(reel);
        const productFromReel = reel.products?.[0];
        const productId =
            productFromReel?.productId !== undefined && productFromReel?.productId !== null && productFromReel?.productId !== ''
                ? String(productFromReel.productId)
                : '';
        setFormData({
            video: null,
            videoPreview: reel.video,
            thumbnail: null,
            thumbnailPreview: reel.thumbnail,
            title: reel.title || "",
            isActive: reel.isActive,
            productId: productId,
            region: reel.region === 'india' ? 'india' : 'qatar',
        });
        if (productFromReel?.name) {
            setSelectedProductInfo(productFromReel);
        } else if (productId) {
            fetchProductDetails(productId);
        } else {
            setSelectedProductInfo(null);
        }
        setShowDialog(true);
    };

    const handleDeleteReel = (reelId) => {
        setReelToDelete(reelId);
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!reelToDelete) return;

        try {
            setDeleteLoading(true);
            const response = await reelAPI.deleteReel(reelToDelete);

            if (response.success) {
                toast.success('Reel deleted successfully!');
                fetchAllActiveReels(activeCurrentPage);
                if (isInactiveExpanded) fetchInactiveReels(inactiveCurrentPage);
                setShowDeleteDialog(false);
                setReelToDelete(null);
            } else {
                toast.error(response.msg || response.error || 'Failed to delete reel');
            }
        } catch (error) {
            console.error('Error deleting reel:', error);
            toast.error('Failed to delete reel');
        } finally {
            setDeleteLoading(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteDialog(false);
        setReelToDelete(null);
    };

    const handleCloseDialog = () => {
        isCancelledRef.current = true;
        setCompressingVideo(false);
        setGeneratingThumbnail(false);
        try {
            ffmpegRef.current?.terminate?.();
        } catch { }
        ffmpegRef.current = null;
        setFfmpegLoaded(false);
        resetReelForm();
        setShowDialog(false);
    };

    const resetReelForm = () => {
        setEditMode(false);
        setEditingReel(null);
        setFormData({
            video: null,
            videoPreview: null,
            thumbnail: null,
            thumbnailPreview: null,
            title: "",
            isActive: true,
            productId: "",
            region: storeRegion,
        });
        setSelectedProductInfo(null);
        setIsProductDialogOpen(false);
        setIsProductViewDialogOpen(false);
        if (formData.videoPreview) URL.revokeObjectURL(formData.videoPreview);
        if (formData.thumbnailPreview) URL.revokeObjectURL(formData.thumbnailPreview);
    };

    const handleSaveReorder = async () => {
        try {
            const changedReels = activeReels.filter(reel => reel.order !== reel.originalOrder)
                .map(reel => ({
                    _id: reel._id,
                    newOrder: reel.order,
                    oldOrder: reel.originalOrder
                }));

            if (changedReels.length === 0) {
                toast.info('No changes to save');
                setIsReorderMode(false);
                return;
            }

            const response = await reelAPI.reorderReels({ reels: changedReels });

            if (response.success) {
                toast.success('Reels reordered successfully!');
                setIsReorderMode(false);
                fetchAllActiveReels(activeCurrentPage);
            } else {
                toast.error(response.msg || 'Failed to reorder reels');
            }
        } catch (error) {
            console.error('Error saving reorder:', error);
            toast.error('Failed to reorder reels');
        }
    };

    const handleExitReorderMode = () => {
        setIsReorderMode(false);
        fetchAllActiveReels(activeCurrentPage);
        if (isInactiveExpanded) fetchInactiveReels(inactiveCurrentPage);
    };

    const handleDragStart = (e, reelId) => {
        e.dataTransfer.setData('text/plain', reelId);
        setDraggedReel(reelId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, reelId) => {
        e.preventDefault();
        if (draggedReel !== reelId) {
            setDragOverReel(reelId);
        }
    };

    const handleDragLeave = () => {
        setDragOverReel(null);
    };

    const handleDrop = (e, targetReelId) => {
        e.preventDefault();
        if (!draggedReel || draggedReel === targetReelId) {
            setDraggedReel(null);
            setDragOverReel(null);
            return;
        }

        const draggedIndex = activeReels.findIndex(r => r._id === draggedReel);
        const targetIndex = activeReels.findIndex(r => r._id === targetReelId);

        if (draggedIndex === -1 || targetIndex === -1) return;

        const newReels = [...activeReels];
        const draggedOrder = newReels[draggedIndex].order;
        const targetOrder = newReels[targetIndex].order;

        newReels[draggedIndex] = { ...newReels[draggedIndex], order: targetOrder };
        newReels[targetIndex] = { ...newReels[targetIndex], order: draggedOrder };
        [newReels[draggedIndex], newReels[targetIndex]] = [newReels[targetIndex], newReels[draggedIndex]];

        setActiveReels(newReels);
        setDraggedReel(null);
        setDragOverReel(null);
    };

    const handleDragEnd = () => {
        setDraggedReel(null);
        setDragOverReel(null);
    };

    const openVideoPreview = (videoUrl) => {
        setSelectedVideo(videoUrl);
        setShowVideoModal(true);
    };

    if (!canView) {
        return (
            <div className="reel-video-container">
                <div className="reel-empty-state">
                    <p>You do not have permission to view reel videos.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="reel-video-container">
            <div className="reel-page-header">
                <div className="reel-page-title">
                    <h1>Reel Videos</h1>
                    <p>Create and manage reel videos for your app</p>
                </div>
                <div className="reel-header-actions">
                    {isReorderMode ? (
                        <div className="reel-reorder-actions">
                            <button
                                className="reel-save-changes-btn"
                                onClick={handleSaveReorder}
                            >
                                <Save size={20} />
                                Save Changes
                            </button>
                            <button
                                className="reel-exit-reorder-btn"
                                onClick={handleExitReorderMode}
                            >
                                <X size={20} />
                                Exit Reorder
                            </button>
                        </div>
                    ) : (
                        <div className="reel-normal-actions">
                            {canEdit && (
                                <button
                                    className="reel-reorder-mode-btn"
                                    onClick={() => setIsReorderMode(true)}
                                >
                                    <GripVertical size={20} />
                                    Reorder
                                </button>
                            )}
                            {canCreate && (
                                <button
                                    className="reel-create-btn"
                                    onClick={handleCreate}
                                >
                                    <Plus size={20} />
                                    Create New Reel
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {isReorderMode && (
                <div className="reel-reorder-instructions">
                    <p>💡 Drag and drop reels to reorder them. Click "Save Changes" when you're done.</p>
                </div>
            )}

            <div className="reel-list-section">
                <div className="reel-group">
                    <h2 className="reel-group-title">Active Reels ({activeCount})</h2>
                    {loadingReels ? (
                        <div className="reel-loading-state">
                            <div className="reel-loading-spinner"></div>
                            <p>Loading Active Reels...</p>
                        </div>
                    ) : (activeReels.length === 0 ? (
                        <div className="reel-empty-state">No active reels found</div>
                    ) : (
                        <>
                            <div className="reel-grid">
                                {activeReels.map((reel) => (
                                    <div key={reel._id} className={`reel-card ${isReorderMode ? 'reel-reorder-mode' : ''} ${draggedReel === reel._id ? 'reel-dragging' : ''} ${dragOverReel === reel._id ? 'reel-drag-over' : ''}`}
                                        draggable={isReorderMode}
                                        onDragStart={(e) => handleDragStart(e, reel._id)}
                                        onDragOver={(e) => handleDragOver(e, reel._id)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, reel._id)}
                                        onDragEnd={handleDragEnd}
                                    >
                                        {isReorderMode && (
                                            <div className="reel-drag-handle">
                                                <GripVertical size={20} />
                                            </div>
                                        )}

                                        <div className="reel-thumbnail">
                                            <video
                                                src={reel.thumbnail || reel.video}
                                                muted
                                                playsInline
                                                preload="metadata"
                                                loading="lazy"
                                                className="reel-thumb-video"
                                            />
                                            {!isReorderMode && (
                                                <div className="reel-play-icon" onClick={() => openVideoPreview(reel.video)}>
                                                    <Video size={22} />
                                                </div>
                                            )}
                                        </div>

                                        <div className="reel-info">
                                            <div className="reel-title">
                                                {reel.title || ""}
                                            </div>
                                            <div className="reel-stats">
                                                <div className="reel-stat-item">
                                                    <span className="reel-stat-label">Views</span>
                                                    <span className="reel-stat-value">{reel.totalViews || 0}</span>
                                                </div>
                                                <div className="reel-stat-item">
                                                    <span className="reel-stat-label">Likes</span>
                                                    <span className="reel-stat-value">{reel.totalLikes || 0}</span>
                                                </div>
                                                <div className="reel-stat-item">
                                                    <span className="reel-stat-label">Shares</span>
                                                    <span className="reel-stat-value">{reel.totalShares || 0}</span>
                                                </div>
                                                <div className="reel-stat-item">
                                                    <span className="reel-stat-label">Comments</span>
                                                    <span className="reel-stat-value">{reel.totalComments || 0}</span>
                                                </div>
                                            </div>
                                            <div className="reel-actions">
                                                {!isReorderMode && (
                                                    <div className="reel-stat-actions">
                                                        <button className="reel-action-btn reel-share-btn" onClick={() => handleShareReel(reel)} title="Share Reel">
                                                            <Share2 size={16} />
                                                        </button>
                                                        {canEdit && (
                                                            <button className="reel-action-btn reel-edit-btn" onClick={() => handleEdit(reel)}>
                                                                <Edit size={16} />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button className="reel-action-btn reel-delete-btn" onClick={() => handleDeleteReel(reel._id)}>
                                                                <Trash size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {!isReorderMode && activePagination.totalPages > 1 && (
                                <div className="reel-pagination">
                                    <button
                                        onClick={handleActivePrevPage}
                                        disabled={!activePagination.hasPrev || loadingReels}
                                        className="reel-pagination-btn"
                                    >
                                        Previous
                                    </button>
                                    <span className="reel-pagination-info">
                                        Page {activePagination.currentPage} of {activePagination.totalPages}
                                    </span>
                                    <button
                                        onClick={handleActiveNextPage}
                                        disabled={!activePagination.hasNext || loadingReels}
                                        className="reel-pagination-btn"
                                    >
                                        {loadingReels ? 'Loading...' : 'Next'}
                                    </button>
                                </div>
                            )}
                        </>
                    ))}
                </div>

                <div className="reel-group inactive-reel-group">
                    <div className="inactive-reel-header" onClick={handleToggleInactive}>
                        <h2 className="reel-group-title">Inactive Reels ({inactiveCount})</h2>
                        <button className="reel-toggle-btn">
                            {isInactiveExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        </button>
                    </div>

                    {isInactiveExpanded && (
                        <>
                            {isInactiveLoading ? (
                                <div className="reel-loading-state">
                                    <div className="reel-loading-spinner"></div>
                                    <p>Loading Inactive Reels...</p>
                                </div>
                            ) : inactiveReels.length === 0 ? (
                                <div className="reel-empty-state">No Inactive reels found</div>
                            ) : (
                                <>
                                    <div className="reel-grid">
                                        {inactiveReels.map((reel) => (
                                            <div key={reel._id} className="reel-card">
                                                <div className="reel-thumbnail">
                                                    <video
                                                        src={reel.thumbnail || reel.video}
                                                        muted
                                                        playsInline
                                                        preload="metadata"
                                                        loading="lazy"
                                                        className="reel-thumb-video"
                                                    />
                                                    <div className="reel-play-icon" onClick={() => openVideoPreview(reel.video)}>
                                                        <Video size={22} />
                                                    </div>
                                                </div>

                                                <div className="reel-info">
                                                    <div className="reel-title">{reel.title || ""}</div>
                                                    <div className="reel-stats">
                                                        <div className="reel-stat-item">
                                                            <span className="reel-stat-label">Views</span>
                                                            <span className="reel-stat-value">{reel.totalViews || 0}</span>
                                                        </div>
                                                        <div className="reel-stat-item">
                                                            <span className="reel-stat-label">Likes</span>
                                                            <span className="reel-stat-value">{reel.totalLikes || 0}</span>
                                                        </div>
                                                        <div className="reel-stat-item">
                                                            <span className="reel-stat-label">Shares</span>
                                                            <span className="reel-stat-value">{reel.totalShares || 0}</span>
                                                        </div>
                                                        <div className="reel-stat-item">
                                                            <span className="reel-stat-label">Comments</span>
                                                            <span className="reel-stat-value">{reel.totalComments || 0}</span>
                                                        </div>
                                                    </div>
                                                    <div className="reel-actions">
                                                        <div className="reel-stat-actions">
                                                            <button className="reel-action-btn reel-share-btn" onClick={() => handleShareReel(reel)} title="Share Reel">
                                                                <Share2 size={16} />
                                                            </button>
                                                            {canEdit && (
                                                                <button className="reel-action-btn reel-edit-btn" onClick={() => handleEdit(reel)}>
                                                                    <Edit size={16} />
                                                                </button>
                                                            )}
                                                            {canDelete && (
                                                                <button className="reel-action-btn reel-delete-btn" onClick={() => handleDeleteReel(reel._id)}>
                                                                    <Trash size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {inactivePagination.totalPages > 1 && (
                                        <div className="reel-pagination">
                                            <button
                                                onClick={handleInactivePrevPage}
                                                disabled={!inactivePagination.hasPrev || isInactiveLoading}
                                                className="reel-pagination-btn"
                                            >
                                                Previous
                                            </button>
                                            <span className="reel-pagination-info">
                                                Page {inactivePagination.currentPage} of {inactivePagination.totalPages}
                                            </span>
                                            <button
                                                onClick={handleInactiveNextPage}
                                                disabled={!inactivePagination.hasNext || isInactiveLoading}
                                                className="reel-pagination-btn"
                                            >
                                                {isInactiveLoading ? 'Loading...' : 'Next'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showVideoModal && (
                <div className="reel-dialog-overlay">
                    <div className="reel-dialog video-preview-dialog">
                        <div className="reel-dialog-header">
                            <h2>Preview Video</h2>
                            <button className="reel-dialog-close" onClick={() => setShowVideoModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="reel-dialog-content">
                            <video
                                src={selectedVideo}
                                controls
                                autoPlay={false}
                                style={{ width: "100%", height: "330px", borderRadius: "10px", paddingBottom: "20px" }}
                            />
                        </div>
                        <div className="reel-dialog-footer">
                        </div>
                    </div>
                </div>
            )}

            {showDialog && (
                <div className="reel-dialog-overlay">
                    <div className="reel-dialog">
                        <div className="reel-dialog-header">
                            <h2>{editMode ? "Update Reel" : "Create Reel"}</h2>
                            <button className="reel-dialog-close" onClick={handleCloseDialog}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="reel-dialog-content">
                            {!editMode && (
                                <>
                                    <div className="reel-form-group">
                                        <label htmlFor="reelVideo">Reel Video</label>
                                        <div className="reel-video-upload-section">
                                            <div className="reel-video-preview">
                                                {compressingVideo ? (
                                                    <div className="reel-video-compress-overlay">
                                                        <div className="reel-loading-spinner"></div>
                                                        <span>Compressing video...</span>
                                                    </div>
                                                ) : formData.videoPreview ? (
                                                    <div className="reel-video-preview-container">
                                                        <video src={formData.videoPreview} controls />
                                                    </div>
                                                ) : (
                                                    <div className="reel-video-placeholder">
                                                        <Video size={32} />
                                                        <span>No video selected</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="reel-video-upload-controls">
                                                <input
                                                    type="file"
                                                    id="reelVideo"
                                                    accept="video/*"
                                                    onChange={handleVideoChange}
                                                    className="reel-file-input"
                                                    disabled={isProcessing}
                                                />
                                                <label htmlFor="reelVideo" className={`reel-upload-btn ${isProcessing ? 'disabled' : ''}`}>
                                                    <Upload size={16} />
                                                    {isProcessing ? 'Processing...' : (formData.videoPreview ? 'Change Video' : 'Choose Video')}
                                                </label>
                                            </div>
                                            <div className="reel-file-size-limit">
                                                Maximum size: 6MB
                                            </div>
                                        </div>
                                    </div>

                                    {generatingThumbnail && (
                                        <div className="reel-thumbnail-generating-alert">
                                            <div className="reel-alert-spinner"></div>
                                            <div className="reel-alert-content">
                                                <strong>Generating Thumbnail Video...</strong>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="reel-form-group">
                                <label htmlFor="reelVideoTitle">Title</label>
                                <input
                                    type="text"
                                    id="reelVideoTitle"
                                    value={formData.title || ""}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Enter reel title"
                                    className="reel-form-input"
                                />
                            </div>

                            <div className="reel-form-group">
                                <label>Product (Optional)</label>
                                <div className="reel-choose-layout">
                                    <button
                                        type="button"
                                        className="reel-choose-btn"
                                        onClick={() => setIsProductDialogOpen(true)}
                                    >
                                        Choose Product
                                    </button>
                                    <div className="reel-selection-summary">
                                        <span className="reel-products-count">
                                            {formData.productId
                                                ? selectedProductInfo?.name
                                                    ? `${selectedProductInfo.name} (ID: ${formData.productId})`
                                                    : `Product ID: ${formData.productId}`
                                                : 'No product selected'}
                                        </span>
                                        {formData.productId && (
                                            <button
                                                type="button"
                                                className="reel-view-btn"
                                                onClick={() => setIsProductViewDialogOpen(true)}
                                            >
                                                View Product
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="reel-form-group reel-active-status-group">
                                <label className="reel-toggle-label-text">Active Status</label>
                                <div className="reel-status-toggle-container">
                                    <label className="reel-status-option">
                                        <input
                                            type="radio"
                                            name="activeStatus"
                                            checked={formData.isActive}
                                            onChange={() => setFormData({ ...formData, isActive: true })}
                                            className="reel-status-radio"
                                        />
                                        <span className="reel-status-option-content reel-active-option">
                                            <Eye size={18} />
                                            <span>Active</span>
                                        </span>
                                    </label>
                                    <label className="reel-status-option">
                                        <input
                                            type="radio"
                                            name="activeStatus"
                                            checked={!formData.isActive}
                                            onChange={() => setFormData({ ...formData, isActive: false })}
                                            className="reel-status-radio"
                                        />
                                        <span className="reel-status-option-content reel-inactive-option">
                                            <X size={18} />
                                            <span>Inactive</span>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="reel-dialog-footer">
                            <button className="reel-cancel-btn" onClick={handleCloseDialog}>
                                Cancel
                            </button>
                            <button
                                className="reel-save-btn"
                                onClick={handleSaveReel}
                                disabled={savingReel || generatingThumbnail || compressingVideo}
                            >
                                {savingReel ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update Reel" : "Create Reel")}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {isProductDialogOpen && (
                <div className="reel-dialog-overlay" onClick={() => setIsProductDialogOpen(false)}>
                    <div className="reel-product-view-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="reel-product-view-header">
                            <h2>Select Product</h2>
                            <button className="reel-dialog-close" onClick={() => setIsProductDialogOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="reel-product-view-content">
                            <input
                                type="text"
                                className="reel-product-search-input"
                                placeholder="Search products by name..."
                                value={productSearch}
                                onChange={(e) => searchProducts(e.target.value)}
                                style={{ width: '100%', padding: '10px 12px', marginBottom: 12, border: '1px solid #d1d5db', borderRadius: 8 }}
                            />
                            {searchingProducts && <p>Searching...</p>}
                            {!searchingProducts && productResults.length === 0 && productSearch.length >= 2 && (
                                <p className="reel-product-view-empty">No products found</p>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                                {productResults.map((product) => (
                                    <button
                                        key={product._id}
                                        type="button"
                                        className="reel-selected-product-item"
                                        onClick={() => handleProductSave(product)}
                                        style={{ cursor: 'pointer', textAlign: 'left', border: '1px solid #e5e7eb', background: '#fff' }}
                                    >
                                        <div className="reel-selected-product-media">
                                            {product.images?.[0]?.thumbnail || product.images?.[0]?.image ? (
                                                <img src={product.images[0].thumbnail || product.images[0].image} alt={product.name} />
                                            ) : (
                                                <div className="reel-selected-product-no-image">No Image</div>
                                            )}
                                        </div>
                                        <div className="reel-selected-product-details">
                                            <span className="reel-selected-product-name">{product.name}</span>
                                            <span className="reel-selected-product-id">ID: {product.productId}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                            {formData.productId && (
                                <button
                                    type="button"
                                    className="reel-remove-product-btn"
                                    style={{ marginTop: 12 }}
                                    onClick={() => handleProductSave(null)}
                                >
                                    Clear selection
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isProductViewDialogOpen && (
                <div className="reel-dialog-overlay" onClick={() => setIsProductViewDialogOpen(false)}>
                    <div className="reel-product-view-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="reel-product-view-header">
                            <h2>Selected Product</h2>
                            <button className="reel-dialog-close" onClick={() => setIsProductViewDialogOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="reel-product-view-content">
                            {selectedProductInfo ? (
                                <div className="reel-selected-product-item">
                                    <div className="reel-selected-product-media">
                                        {selectedProductInfo.images?.length > 0 ? (
                                            <img src={selectedProductInfo.images[0].image} alt={selectedProductInfo.name} />
                                        ) : selectedProductInfo.image ? (
                                            <img src={selectedProductInfo.image} alt={selectedProductInfo.name} />
                                        ) : (
                                            <div className="reel-selected-product-no-image">No Image</div>
                                        )}
                                    </div>
                                    <div className="reel-selected-product-details">
                                        <span className="reel-selected-product-name">{selectedProductInfo.name}</span>
                                        {selectedProductInfo.category && (
                                            <span className="reel-selected-product-category">{selectedProductInfo.category}</span>
                                        )}
                                        <span className="reel-selected-product-id">ID: {formData.productId}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="reel-remove-product-btn"
                                        onClick={removeSelectedProduct}
                                        title="Remove product"
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                            ) : (
                                <p className="reel-product-view-empty">No product details available</p>
                            )}
                        </div>
                        <div className="reel-dialog-footer">
                            <button className="reel-save-btn" onClick={() => setIsProductViewDialogOpen(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteDialog && (
                <div className="reel-dialog-overlay">
                    <div className="reel-dialog">
                        <div className="reel-dialog-header">
                            <h2>Confirm Delete</h2>
                            <button className="reel-dialog-close" onClick={cancelDelete}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="reel-dialog-content" style={{ paddingBottom: "20px" }}>
                            <p>
                                Are you sure you want to delete this reel?
                                This action cannot be undone.
                            </p>
                        </div>

                        <div className="reel-dialog-footer">
                            <button
                                className="reel-cancel-btn"
                                onClick={cancelDelete}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="reel-delete-confirm-btn"
                                onClick={confirmDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReelVideos;